import React, { useState, useMemo } from 'react';
import { columnType, dataType, Order, OrderBy, tableState, initialTableState } from './types';
import { getComparator, stableSort } from './sorts';
import { BaseTable } from './BaseTable';
import ClientTablePagination from './sections/ClientTablePagination';
import { BasicTableStyle } from './style';

export interface IClientPaginatedTableProps {
	data: dataType[];
	columns: columnType[];
	rowAction?: (row: dataType) => void;
	rowsClass?: string;
	tableClass?: string;
	defaultSortBy?: string;
	defaultOrderBy?: Order;
	hideHeader?: boolean;
	noShrink?: boolean;
	dateFormat?: string;
	perPage?: number;
	noPagination?: boolean;
}

export const ClientPaginatedTable: React.FC<IClientPaginatedTableProps> = ({
	data,
	columns,
	rowAction,
	rowsClass,
	tableClass,
	defaultSortBy,
	defaultOrderBy,
	hideHeader,
	noShrink,
	dateFormat,
	perPage = 10,
	noPagination = false,
}) => {
	const [tableState, setTableState] = useState<tableState>({
		...initialTableState,
		sortBy: defaultSortBy || '',
		orderBy: defaultOrderBy || (OrderBy.DESENDING as Order),
		perPage: perPage,
	});

	const [currentPage, setCurrentPage] = useState(0);

	// Sort and slice data for current page
	const displayData = useMemo(() => {
		const { orderBy, sortBy } = tableState;
		const sortedData = stableSort(data, getComparator(orderBy, sortBy || ''));

		if (noPagination) {
			return sortedData;
		}

		const startIndex = currentPage * perPage;
		const endIndex = startIndex + perPage;
		return sortedData.slice(startIndex, endIndex);
	}, [data, tableState, currentPage, perPage, noPagination]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	// Reset to first page when data changes
	React.useEffect(() => {
		setCurrentPage(0);
	}, [data.length]);

	return (
		<BasicTableStyle spacing={3}>
			<BaseTable
				data={displayData}
				columns={columns}
				rowAction={rowAction}
				rowsClass={rowsClass}
				tableClass={tableClass}
				defaultSortBy={defaultSortBy}
				defaultOrderBy={defaultOrderBy}
				hideHeader={hideHeader}
				noShrink={noShrink}
				dateFormat={dateFormat}
				tableState={tableState}
				setTableState={setTableState}
			/>
			{!noPagination && (
				<ClientTablePagination
					total={data.length}
					currentPage={currentPage}
					perPage={perPage}
					onPageChange={handlePageChange}
				/>
			)}
		</BasicTableStyle>
	);
};

export default ClientPaginatedTable;
